import { useAdminOverview } from '@church/data'
import { formatCents, type Cents } from '@church/domain'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Card, ErrorNote, PageHeader, Spinner } from '../components/ui.tsx'
import { ago, bytes } from '../components/platform/admin-format.ts'

/** Every church at a glance: plans, revenue, activity, ForStream usage. */
export const Route = createFileRoute('/_authed/admin/')({
  component: AdminOverview,
})

function AdminOverview() {
  const overview = useAdminOverview()
  const t = overview.data?.totals
  if (overview.isPending) return <Spinner />
  return (
    <div className="space-y-6">
      <PageHeader visible title="Churches" />
      <ErrorNote error={overview.error} />
      {t && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
          {[
            ['Churches', t.organizations.toLocaleString()],
            ['New in 30 days', String(t.newLast30Days)],
            ['Active in 30 days', String(t.activeLast30Days)],
            ['On trial', String(t.trialing)],
            ['Paying', String(t.paying)],
            ['Payment failed', String(t.pastDue)],
            ['Monthly revenue', formatCents(t.monthlyRecurringCents as Cents).replace(/\.00$/, '')],
            ['Streamed this month', `${t.streamHoursThisMonth.toLocaleString()} h`],
          ].map(([label, value]) => (
            <Card key={label} className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-content-subtle">
                {label}
              </p>
              <p className="mt-1 text-xl font-bold tabular-nums text-content-default">{value}</p>
            </Card>
          ))}
        </div>
      )}
      {overview.data && (
        <Card className="flex flex-wrap gap-x-8 gap-y-2 p-4 text-sm">
          {overview.data.byPlan.map((p) => (
            <span key={p.key}>
              <span className="font-medium text-content-default">{p.name}</span>{' '}
              <span className="text-content-muted">
                {p.churches} {p.churches === 1 ? 'church' : 'churches'}
                {p.paying ? `, ${p.paying} paying` : ''}
              </span>
            </span>
          ))}
        </Card>
      )}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-content-muted">
            <tr className="border-b border-border-subtle">
              <th className="px-4 py-2 font-medium">Church</th>
              <th className="px-4 py-2 font-medium">Plan</th>
              <th className="px-4 py-2 text-right font-medium">People</th>
              <th className="px-4 py-2 text-right font-medium">Staff</th>
              <th className="px-4 py-2 text-right font-medium">Streamed</th>
              <th className="px-4 py-2 text-right font-medium">Media</th>
              <th className="px-4 py-2 font-medium">Last active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {overview.data?.organizations.map((o) => (
              <tr key={o.id} className="hover:bg-surface-sunken">
                <td className="px-4 py-2">
                  <Link
                    to="/admin/churches/$id"
                    params={{ id: o.id }}
                    className="block font-medium text-content-default hover:text-content-brand"
                  >
                    {o.name}
                  </Link>
                  <span className="text-xs text-content-muted">
                    {o.slug}
                    {o.deletionScheduledFor ? ' · deletion scheduled' : ''}
                  </span>
                </td>
                <td className="px-4 py-2 text-content-default">
                  {o.planName}
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs ${o.readOnly ? 'bg-danger-soft text-danger-content' : o.status === 'past_due' ? 'bg-warning-soft text-warning-content' : 'bg-surface-sunken text-content-muted'}`}
                  >
                    {o.readOnly
                      ? 'read-only'
                      : o.limited
                        ? o.limited.replace('_', ' ')
                        : o.status.replace('_', ' ')}
                  </span>
                  {o.overridden && (
                    <span className="ml-1 rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-content-muted">
                      override
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-content-muted">
                  {o.people.toLocaleString()}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-content-muted">{o.staff}</td>
                <td className="px-4 py-2 text-right tabular-nums text-content-muted">
                  {o.streamHoursThisMonth} h
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-content-muted">
                  {bytes(o.streamStorageBytes)}
                </td>
                <td className="px-4 py-2 text-content-muted">{ago(o.lastActivityAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
